/**
 * Add chart / Edit chart for a rule (T3): Explore, in a frame, as a
 * dashboard's chart builder is (`dashboard/chart-builder.ts`), with the same
 * look. Anything Explore can draw, a rule's alert can show.
 *
 * The frame opens on a ship and a window so there is something to look at -
 * the rule's newest backtest, on the ship where it found the most - and starts
 * from Argus's guess when the chart is new. Neither is kept: the alert sets
 * the ship and the window. While the author builds, a line chart in the frame
 * shows the backtest's episodes and the rule's numbers, as the alert's page
 * will (through the frame's overlay registry, `builder-frame.ts`).
 *
 * The dialog saves nothing. It hands `{title, query}` to the editor, which
 * stores the rule's list (`argus-rules-store.ts` `setCharts`).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { getArgusBacktest, getArgusRuleBacktest } from '../../api/endpoints.ts';
import type { ArgusBacktest, ArgusRuleChart } from '../../api/types.ts';
import { stripContext } from '../../state/presets.ts';
import { builderLaunchParams } from '../../state/embed.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';
import { suggestTileTitle } from '../dashboard/tile-spec.ts';
import { BUILDER_CHANGE, type BuilderFrameApi } from '../dashboard/builder-frame.ts';
import { episodeSpans } from './argus-backtest-chart.ts';
import { lineShapes, type EpisodeSpan, type RuleLine } from './argus-chart-marks.ts';

/** What the frame opens on: a ship, a window, and what to mark on it. */
export interface ChartSeed {
  entity: string | null;
  /** The entity as people know it: "AIDAblu", not "A-BL". */
  entityName: string | null;
  start: string | null;
  end: string | null;
  left: string[];
  right: string[];
  lines: RuleLine[];
  episodes: EpisodeSpan[];
}

const NO_SEED: ChartSeed = { entity: null, entityName: null, start: null, end: null, left: [], right: [], lines: [], episodes: [] };

/** The ship to show a run on: the one where it found the most, else the first it read. */
export function seedFromRun(run: ArgusBacktest | null): ChartSeed {
  const result = run?.result;
  if (!run || !result) return NO_SEED;
  const counts = new Map<string, number>();
  for (const e of result.episodes) counts.set(e.entity, (counts.get(e.entity) ?? 0) + 1);
  const readable = result.entities.filter((e) => !e.error && run.chart?.[e.entity]);
  const best = [...readable].sort((a, b) => (counts.get(b.entity) ?? 0) - (counts.get(a.entity) ?? 0))[0];
  if (!best) return NO_SEED;
  const spec = run.chart![best.entity]!;
  return {
    entity: best.entity,
    entityName: best.name || best.entity,
    start: result.range_from.slice(0, 19),
    end: result.range_to.slice(0, 19),
    left: spec.left,
    right: spec.right,
    lines: spec.lines,
    episodes: episodeSpans(result.episodes.filter((e) => e.entity === best.entity)),
  };
}

/** The frame's first query: the chart (or the guess), on the seed's ship and window. */
export function seedQuery(chart: ArgusRuleChart | null, seed: ChartSeed, source: string): string {
  const params = new URLSearchParams(chart?.query ?? '');
  if (!chart) {
    if (seed.left.length) params.set('yl', seed.left.join(','));
    if (seed.right.length) params.set('yr', seed.right.join(','));
  }
  if (source && source !== DEFAULT_SOURCE && !params.has('ds')) params.set('ds', source);
  if (seed.entity) params.set('ship', seed.entity);
  if (seed.start) params.set('start', seed.start);
  if (seed.end) params.set('end', seed.end);
  return params.toString();
}

@customElement('argus-chart-dialog')
export class ArgusChartDialog extends LightElement {
  @property() ruleId = '';
  @property() ruleName = '';
  @property() source = '';
  /** The chart being edited, or null for a new one. */
  @property({ attribute: false }) chart: ArgusRuleChart | null = null;

  @state() private chartTitle = '';
  @state() private frameSrc = '';
  @state() private ready = false;
  @state() private seed: ChartSeed = NO_SEED;
  private closing = false;
  private previewing = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.chartTitle = this.chart?.title ?? '';
    window.addEventListener('message', this.onMessage);
    document.addEventListener('keydown', this.onKeydown);
    void this.open();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('message', this.onMessage);
    document.removeEventListener('keydown', this.onKeydown);
  }

  /** Read the newest backtest for a ship and a window, then build the frame once. */
  private async open(): Promise<void> {
    let seed = NO_SEED;
    try {
      const newest = (await getArgusRuleBacktest(this.ruleId)).backtest;
      // The newest-run endpoint leaves out the chart spec; one more read has it.
      if (newest?.status === 'done') seed = seedFromRun(await getArgusBacktest(newest.id));
    } catch {
      // No backtest to start from: the frame opens without a ship, and asks.
    }
    this.seed = seed;
    const params = builderLaunchParams({ livePreset: null, historyStart: seed.start, historyEnd: seed.end });
    for (const [k, v] of new URLSearchParams(seedQuery(this.chart, seed, this.source))) params.set(k, v);
    const ff = new URLSearchParams(window.location.search).get('ff');
    if (ff) params.set('ff', ff);
    params.set('tab', 'plot');
    this.frameSrc = `/explore/plot?${params.toString()}`;
  }

  private frameApi(): BuilderFrameApi | null {
    const frame = this.querySelector<HTMLIFrameElement>('iframe.cbuilder-frame');
    try {
      return frame?.contentWindow?.__ndeBuilder ?? null;
    } catch {
      return null;
    }
  }

  private onMessage = (e: MessageEvent): void => {
    if (e.origin !== window.location.origin) return;
    if ((e.data as { type?: string } | null)?.type !== BUILDER_CHANGE) return;
    this.ready = true;
    this.preview();
    this.requestUpdate();
  };

  /** The backtest's marks on the frame's line chart, once the frame can take them. */
  private preview(): void {
    const api = this.frameApi();
    if (this.previewing || !api?.overlay) return;
    this.previewing = true;
    const { lines, episodes } = this.seed;
    api.overlay((ctx) => (ctx.plotType === 'line' ? lineShapes(lines, episodes, ctx.left, ctx.right) : []));
  }

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.cancel();
  };

  private cancel(): void {
    if (this.closing) return;
    this.closing = true;
    this.dispatchEvent(new CustomEvent('chart-dialog-close', { bubbles: true, composed: true }));
  }

  private save(): void {
    const api = this.frameApi();
    if (this.closing || !api?.canSave()) return;
    this.closing = true;
    const recipe = new URLSearchParams(stripContext(api.query()));
    recipe.delete('ff');
    const query = recipe.toString();
    const chart: ArgusRuleChart = { title: this.chartTitle.trim() || suggestTileTitle(query), query };
    this.dispatchEvent(new CustomEvent<ArgusRuleChart>('chart-dialog-save', { detail: chart, bubbles: true, composed: true }));
  }

  override render(): TemplateResult {
    const api = this.ready ? this.frameApi() : null;
    const canSave = !!api?.canSave();
    const placeholder = api ? suggestTileTitle(stripContext(api.query())) : '';
    return html`
      <div class="cbuilder-scrim" @click=${() => this.cancel()}></div>
      <div class="cbuilder argus-cd" role="dialog" aria-modal="true" aria-label=${this.chart ? 'Edit chart' : 'Add chart'}>
        <header class="cbuilder-head">
          <div class="cbuilder-heading">
            <h2>${this.chart ? 'Edit chart' : 'Add chart'} <span class="argus-cd-rule">for ${this.ruleName}</span></h2>
            <input
              class="cbuilder-title"
              placeholder=${placeholder || 'Chart title'}
              .value=${this.chartTitle}
              aria-label="Chart title"
              @input=${(e: Event) => (this.chartTitle = (e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="cbuilder-head-actions">
            <button class="dash-btn" @click=${() => this.cancel()}>Cancel</button>
            <button
              class="dash-btn is-primary"
              ?disabled=${!canSave}
              title=${canSave ? '' : 'Pick a ship and at least one series first'}
              @click=${() => this.save()}
            >
              ${this.chart ? 'Save chart' : 'Add chart'}
            </button>
          </div>
        </header>
        <p class="argus-cd-note" role="note">
          ${this.seed.entity
            ? html`Shown here on <b>${this.seed.entityName}</b>, over the newest backtest. On an alert's page, the
                alert sets the ship and the window. A line chart shows the episodes and the rule's numbers.`
            : html`On an alert's page, the alert sets the ship and the window. Backtest the rule to preview its
                episodes here.`}
        </p>
        <div class="cbuilder-body">
          ${this.ready ? nothing : html`<div class="cbuilder-loading" aria-live="polite">Opening the editor…</div>`}
          ${this.frameSrc ? html`<iframe class="cbuilder-frame" title="Chart editor" src=${this.frameSrc}></iframe>` : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-chart-dialog': ArgusChartDialog;
  }
}
